import { Form } from "@unform/web";
import React, { useEffect, useRef, useState } from "react";
import { BiArrowBack } from "react-icons/bi";
import { IoMdCheckmark } from "react-icons/io";
import { Link, useHistory, useParams } from "react-router-dom";
import { useSelector } from 'react-redux';
import { toast } from "react-toastify";
import * as Yup from "yup";
import DivDiagonal from "../../../components/DivDiagonal";
import CustomButton from "../../../components/Form/Button";
import Input from "../../../components/Form/Input";
import messageTypes from "../../../constants/MessagesTypes";
import { createOperator, updateOperator, getOperator } from "../../../services/Operator";
import { getCompany } from "../../../services/Company";
import {
  OperatorNewWrapper,
  OperatorCard,
  CardTitle,
  FormWrapper,
  StyledFormWrapper,
  ContentArea,
  ButtonContainer,
} from "./styles";

export default function NewOperator() {
  const [loading, setLoading] = useState(false);
  const [operator, setOperator] = useState(undefined);
  const [company, setCompany] = useState(null);
  const formRef = useRef(null);
  const history = useHistory();
  const { companyId, id } = useParams();
  const { isAdmin } = useSelector((state) => state.CompanyReducer || { isAdmin: false });

  async function handleSubmit(data) {
    try {
      if (loading) {
        return;
      }

      if (!isAdmin) {
        toast.error('Apenas administradores podem criar/editar funcionários.');
        return;
      }

      setLoading(true);
      formRef.current.setErrors({});
      const schema = Yup.object().shape({
        name: Yup.string().required(messageTypes.REQUIRED_FIELD),
        username: Yup.string().email('Email inválido').required(messageTypes.REQUIRED_FIELD),
        password: id ? Yup.string() : Yup.string().required(messageTypes.REQUIRED_FIELD),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      const operatorData = {
        name: data.name,
        username: data.username,
        companyId: companyId,
      };

      if (data.password && data.password.trim() !== '') {
        operatorData.password = data.password;
      }

      const response = id 
        ? await updateOperator(id, operatorData) 
        : await createOperator(operatorData);

      if (response && response.status === 200) {
        toast.success(id ? messageTypes.SUCCESS_UPDATE : messageTypes.SUCCESS);
        history.push(`/operator/${companyId}`);
      } else {
        toast.error(id ? messageTypes.ERROR_UPDATE : messageTypes.ERROR_SAVE);
      }

      setLoading(false);
    } catch (err) {
      setLoading(false);
      if (err instanceof Yup.ValidationError) {
        const validationErrors = {};
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });
        formRef.current.setErrors(validationErrors);
        toast.warning(messageTypes.REQUIRED_FIELDS);
        return;
      }

      toast.error(err.response?.data?.message || messageTypes.ERROR);
    }
  }

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Apenas administradores podem acessar esta página.');
      history.push('/dashboard');
      return;
    }

    if (!companyId) {
      toast.error('ID da empresa é obrigatório.');
      history.push('/company');
      return;
    }

    // Carregar dados da empresa
    getCompany(companyId).then((data) => {
      if (data && data.data && data.data.data) {
        setCompany(data.data.data);
      }
    });

    if (id) {
      // Buscar dados do funcionário
      setLoading(true);
      getOperator(id).then((data) => {
        if (data && data.data && data.data.data) {
          const operatorData = data.data.data;
          setOperator({
            id: operatorData.id,
            name: operatorData.name || '',
            username: operatorData.email || operatorData.username || '',
            password: '', // Não carregar senha por segurança
            active: operatorData.active !== undefined ? operatorData.active : true,
          });
        } else {
          setOperator({ id, name: '', username: '', password: '', active: true });
        }
        setLoading(false);
      }).catch(() => {
        setOperator({ id, name: '', username: '', password: '', active: true });
        setLoading(false);
      });
    } else {
      setOperator(null);
      setLoading(false);
    }
  }, [id, companyId, isAdmin, history]);

  if (!isAdmin) {
    return null;
  }

  return (
    <OperatorNewWrapper>
      <DivDiagonal />
      <ContentArea>
        {operator !== undefined && (
          <OperatorCard>
            <StyledFormWrapper>
              <Form ref={formRef} onSubmit={handleSubmit} initialData={operator}>
                <CardTitle>
                  {id ? (
                    <>
                      <i className="fas fa-edit" /> Editar Funcionário
                    </>
                  ) : (
                    <>
                      <i className="fas fa-plus" /> Novo Funcionário
                    </>
                  )}
                </CardTitle>

                {company && (
                  <div style={{ 
                    marginBottom: '16px', 
                    padding: '10px', 
                    background: 'rgba(242, 146, 5, 0.1)', 
                    borderRadius: '8px', 
                    border: '1px solid rgba(242, 146, 5, 0.3)',
                    fontSize: '13px'
                  }}>
                    <p style={{ margin: 0, color: '#f29205', fontSize: '13px', fontWeight: 600 }}>
                      Empresa: <span style={{ color: '#ffffff' }}>{company.name}</span>
                    </p>
                  </div>
                )}

                <div className="row mb-3" style={{ marginBottom: '16px' }}>
                  <div className="col-12">
                    <Input
                      name="name"
                      label="Nome do Funcionário"
                      placeholder="Digite o nome completo"
                    />
                  </div>
                </div>

                <div className="row mb-3" style={{ marginBottom: '16px' }}>
                  <div className="col-12">
                    <Input
                      name="username"
                      type="email"
                      label="Email"
                      placeholder="Digite o email"
                    />
                  </div>
                </div>

                <div className="row mb-3" style={{ marginBottom: '16px' }}>
                  <div className="col-12">
                    <Input
                      name="password"
                      type="password"
                      label={id ? "Nova Senha (deixe em branco para manter)" : "Senha"}
                      placeholder={id ? "Digite uma nova senha ou deixe em branco" : "Digite a senha"}
                    />
                  </div>
                </div>

                <ButtonContainer>
                  <Link to={`/operator/${companyId}`} style={{ textDecoration: 'none' }}>
                    <CustomButton
                      variant="outlined"
                      className="btn btn-secondary font-weight-bold text-capitalize mobile-button"
                      type="button"
                      startIcon={<BiArrowBack/>}
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '2px solid rgba(255, 255, 255, 0.2)',
                        color: '#e0e0e0',
                        padding: '14px 28px',
                        borderRadius: '10px',
                        fontWeight: 600,
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      Voltar
                    </CustomButton>
                  </Link>
                  <CustomButton
                    className="btn btn-primary font-weight-bold text-capitalize mobile-button"
                    loading={loading}
                    type="submit"
                    startIcon={<IoMdCheckmark/>}
                    style={{
                      background: 'linear-gradient(135deg, #f29205 0%, #ff6b35 100%)',
                      border: 'none',
                      color: '#ffffff',
                      padding: '14px 32px',
                      borderRadius: '10px',
                      fontWeight: 600,
                      boxShadow: '0 4px 15px rgba(242, 146, 5, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(242, 146, 5, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(242, 146, 5, 0.3)';
                    }}
                  >
                    Salvar
                  </CustomButton>
                </ButtonContainer>
              </Form>
            </StyledFormWrapper>
          </OperatorCard>
        )}
      </ContentArea>
    </OperatorNewWrapper>
  );
}

